import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ApiError, describeApiFailure } from '../api/http.ts';
import buttons from '../styles/buttons.module.css';
import styles from './ErrorBoundary.module.css';

// The last line of defence: a render that throws would otherwise blank the
// whole page. The panel keeps the person oriented and offers the two ways
// out (reload, or back to the client list). Only an ApiError's message is
// shown, because that text was written for people (and, for a failure the
// server did not expect, carries the reference to quote); any other
// exception is an internal detail that would read as noise.

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

/** The error's message when it was written for the person; null otherwise. */
function shownMessage(error: Error): string | null {
  return error instanceof ApiError ? describeApiFailure(error) : null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }
    const message = shownMessage(error);
    return (
      <div className={styles['panel']} role="alert">
        <h2 className={styles['title']}>Something went wrong</h2>
        <p className={styles['text']}>
          {message ?? 'This page could not be shown. Reloading usually puts it right.'}
        </p>
        <div className={styles['actions']}>
          <button
            className={buttons['primary']}
            type="button"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload
          </button>
          <a className={buttons['secondary']} href="/">
            Go home
          </a>
        </div>
      </div>
    );
  }
}
